import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Search } from "lucide-react";
import RenameRouteDialog from "@/components/routes/RenameRouteDialog";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function RoutePullBar({ onAdd }) {
  const [routes, setRoutes] = useState([]);
  const [route, setRoute] = useState("");
  const [q, setQ] = useState("");
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [picked, setPicked] = useState(new Set());

  const loadRoutes = () => api.get("/route-setup/routes").then((r) => setRoutes(r.data.routes)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { loadRoutes(); }, []);

  const pull = async (params) => {
    setBusy(true);
    try {
      const { data } = await api.get("/route-setup/stops", { params });
      return data.stops;
    } catch (e) { toast.error(formatError(e)); return []; } finally { setBusy(false); }
  };

  const addRoute = async () => {
    const stops = await pull({ route });
    onAdd(stops);
    toast.success(`Pulled ${stops.length} customer${stops.length === 1 ? "" : "s"} from route ${route}`);
  };

  const search = async (e) => { e.preventDefault(); if (q.trim()) { setPicked(new Set()); setFound(await pull({ q: q.trim() })); } };

  return (
    <div className="border rounded-lg bg-card p-4 space-y-3" data-testid="route-pull-bar">
      <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Pull from main database</p>
      <div className="flex flex-wrap gap-2">
        <Select value={route} onValueChange={setRoute}>
          <SelectTrigger className="w-56" data-testid="route-value-select"><SelectValue placeholder="Choose a route" /></SelectTrigger>
          <SelectContent className="bg-card">
            {routes.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`route-value-${r.value}`}>{r.value} · {r.count} customers</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={() => setRenaming(route)} disabled={!route} aria-label="Rename route" title="Rename route" data-testid="route-rename-button"><Pencil size={14} /></Button>
        <Button onClick={addRoute} disabled={!route || busy} data-testid="route-pull-button">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add route customers
        </Button>
        <form onSubmit={search} className="flex gap-2 flex-1 min-w-[240px]">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Contains… (any field in the main database: name, job #, route, address, city, phone)" data-testid="route-search-input" />
          <Button type="submit" variant="outline" disabled={busy || !q.trim()} data-testid="route-search-button"><Search size={14} /></Button>
        </form>
      </div>
      {found && found.length > 0 && (
        <div className="flex items-center gap-3 text-sm" data-testid="route-search-bulk">
          <Checkbox checked={found.every((s) => picked.has(s.record_id))} onCheckedChange={(on) => setPicked(on ? new Set(found.map((s) => s.record_id)) : new Set())} data-testid="route-search-check-all" aria-label="Check all results" />
          <span className="text-muted-foreground">{found.length} found{picked.size ? ` · ${picked.size} checked` : ""}</span>
          <Button size="sm" disabled={!picked.size} onClick={() => { onAdd(found.filter((s) => picked.has(s.record_id))); setPicked(new Set()); }} data-testid="route-search-add-checked"><Plus size={14} /> Add checked ({picked.size})</Button>
        </div>
      )}
      {found && (
        <ul className="max-h-56 overflow-y-auto divide-y border rounded-md text-sm" data-testid="route-search-results">
          {found.length === 0 && <li className="p-3 text-muted-foreground">No customers found</li>}
          {found.map((s) => (
            <li key={s.record_id} className="flex items-center justify-between gap-2 px-3 py-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={picked.has(s.record_id)} onCheckedChange={(on) => setPicked((p) => { const n = new Set(p); on ? n.add(s.record_id) : n.delete(s.record_id); return n; })} data-testid={`route-search-check-${s.record_id}`} />
                <span><span className="font-medium">{s.name || "—"}</span> <span className="text-muted-foreground">· {s.city || "—"} · route {s.route || "—"} · job {s.job || "—"}</span></span>
              </label>
              <Button size="sm" variant="ghost" onClick={() => onAdd([s])} data-testid={`route-search-add-${s.record_id}`}><Plus size={14} /> Add</Button>
            </li>
          ))}
        </ul>
      )}
      <RenameRouteDialog route={renaming} count={routes.find((r) => r.value === renaming)?.count} onClose={() => setRenaming(null)}
        onRenamed={(v) => { setRenaming(null); setRoute(v); loadRoutes(); }} />
    </div>
  );
}
